import React, { useEffect, useRef, useState } from 'react';
import { MessageSquareText, Sparkles, Tags, X } from 'lucide-react';
import { AudienceLevel, ConversationRead, LEVEL_LABELS, LEVEL_ORDER } from '../../types/pathway';
import { TopicEditor } from './TopicEditor';
import { PathwayButton } from './PathwayButton';
import { ResetFiltersButton } from '../ResetFiltersButton';
type EntryMode = 'notes' | 'topics';
interface StartPanelProps {
  mode: EntryMode;
  notes: string;
  topics: string;
  audience: AudienceLevel | 'any';
  liveRead: ConversationRead;
  activeTerms: string[];
  suggestedTerms: string[];
  vocabulary: string[];
  detectedLevel: AudienceLevel | null;
  /** Null until something is matching. */
  matchCount: number | null;
  /** True when there is a search to reset. */
  hasSearch: boolean;
  onReset: () => void;
  onModeChange: (mode: EntryMode) => void;
  onNotesChange: (value: string) => void;
  onTopicsChange: (value: string) => void;
  onAudienceChange: (value: AudienceLevel | 'any') => void;
  onToggleTerm: (term: string) => void;
  onClearNotes: () => void;
  onBrowseAll: () => void;
}
const EXAMPLE = 'Spoke with the VP of People at Meridian Health. They promoted 24 senior nurses into manager roles after a reorg and turnover has spiked. The new managers are still doing the clinical work themselves and avoiding performance conversations.';
export function StartPanel({
  mode,
  notes,
  topics,
  audience,
  liveRead,
  activeTerms,
  suggestedTerms,
  vocabulary,
  detectedLevel,
  matchCount,
  hasSearch,
  onReset,
  onModeChange,
  onNotesChange,
  onTopicsChange,
  onAudienceChange,
  onToggleTerm,
  onClearNotes,
  onBrowseAll
}: StartPanelProps) {
  const tabs: {
    id: EntryMode;
    label: string;
    icon: typeof Tags;
  }[] = [{
    id: 'topics',
    label: 'By topic or skill',
    icon: Tags
  }, {
    id: 'notes',
    label: 'From conversation notes',
    icon: MessageSquareText
  }];
  const typingRef = useRef<number | null>(null);
  const detectedTerms = liveRead.terms;
  // Headcount has no control of its own, so it needs its own dismiss.
  const [dismissedHeadcount, setDismissedHeadcount] = useState<number | null>(null);
  const showHeadcount = liveRead.headcount !== null && liveRead.headcount !== dismissedHeadcount;
  const stopTyping = () => {
    if (typingRef.current !== null) {
      window.clearInterval(typingRef.current);
      typingRef.current = null;
    }
  };
  const clearNotes = () => {
    stopTyping();
    setDismissedHeadcount(null);
    onClearNotes();
  };
  useEffect(() => () => {
    if (typingRef.current !== null) window.clearInterval(typingRef.current);
  }, []);
  const typeExample = () => {
    stopTyping();
    setDismissedHeadcount(null);
    let index = 0;
    onNotesChange('');
    typingRef.current = window.setInterval(() => {
      index = Math.min(index + 4, EXAMPLE.length);
      onNotesChange(EXAMPLE.slice(0, index));
      if (index >= EXAMPLE.length && typingRef.current !== null) {
        window.clearInterval(typingRef.current);
        typingRef.current = null;
      }
    }, 16);
  };
  return <div data-id="element-1966" data-mp="n344k5pfisx8">
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1967" data-mp="n3neaz4xeb06">
        <div className="flex border-b border-gray-200 px-5" data-id="element-1968" data-mp="n5gpmrqhhjei">
          {tabs.map(tab => {
          const active = mode === tab.id;
          return <button key={tab.id} type="button" onClick={() => onModeChange(tab.id)} aria-pressed={active} className={active ? 'flex items-center gap-2 border-b-2 border-[#00638C] pb-4 pt-4 pr-6 text-sm font-medium text-[#00638C] transition-colors' : 'flex items-center gap-2 border-b-2 border-transparent pb-4 pt-4 pr-6 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900'} data-id="element-1969" data-mp="w4bthx622hen">
                <tab.icon size={16} data-id="element-1970" data-mp="3gut35isj7wwk" />
                {tab.label}
              </button>;
        })}
        </div>

        <div className="p-6" data-id="element-1971" data-mp="2k3xs37i7kykd">
          {mode === 'notes' ? <>
              <label htmlFor="conversation-notes" className="block text-sm font-semibold text-gray-900" data-id="element-1972" data-mp="2p2shisaolldg">
                What did the prospect tell you?
              </label>
              <p className="mt-1 text-sm text-gray-600" data-id="element-1973" data-mp="2xvlycu5s0stf">
                Paste your call notes in plain language. Modules start matching
                as you type — no need to press anything.
              </p>
              <textarea id="conversation-notes" value={notes} onChange={e => onNotesChange(e.target.value)} rows={6} placeholder={EXAMPLE} className="mt-3 w-full resize-y rounded-lg border border-gray-200 p-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1974" data-mp="37qdzu92hbzth" />
              <div className="mt-2 flex items-center gap-4" data-id="element-1975" data-mp="1evwfdo8ed7pf">
                <button type="button" onClick={typeExample} className="text-xs font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1976" data-mp="1ex7m9wx52vkr">
                  Use an example
                </button>
                {notes.trim().length > 0 && <button type="button" onClick={clearNotes} className="text-xs font-medium text-gray-500 transition-colors hover:text-[#DB3725]" data-id="element-1977" data-mp="1xtz0hrxtqkhy">
                    Clear notes
                  </button>}
              </div>

              <div className="mt-4 rounded-lg bg-[#30718D]/5 p-4" data-id="element-1978" data-mp="1jv96wma7r2yc">
                <TopicEditor heading={notes.trim().length > 0 ? 'Suggested topics from your notes' : 'Topics in this search'} activeTerms={activeTerms} detectedTerms={detectedTerms} suggestedTerms={suggestedTerms} suggestedLabel={notes.trim().length > 0 ? 'Also consider' : 'Popular topics'} vocabulary={vocabulary} onToggleTerm={onToggleTerm} emptyHint="Write a sentence or two, or add a topic below." footer={showHeadcount ? <div className="mt-4 flex items-start gap-2 border-t border-[#30718D]/15 pt-3" data-id="element-1980" data-mp="3s2urlykvurf8">
                        <p className="flex-1 text-xs text-gray-600" data-id="element-1981" data-mp="3dehrcperimex">
                          Sounds like about {liveRead.headcount} people — pick a
                          cohort later to record the exact size.
                        </p>
                        <button type="button" onClick={() => setDismissedHeadcount(liveRead.headcount)} aria-label="Dismiss the detected headcount" className="flex-shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:text-gray-700" data-id="element-1982" data-mp="34eut2chbxwq3">
                          <X size={12} data-id="element-1983" data-mp="2uoczi3fh4v1n" />
                        </button>
                      </div> : null} data-id="element-1979" data-mp="dedsra1ax6rd" />
              </div>
            </> : <>
              <label htmlFor="topic-input" className="block text-sm font-semibold text-gray-900" data-id="element-1984" data-mp="33orphg3duybh">
                Topics or skills
              </label>
              <p className="mt-1 text-sm text-gray-600" data-id="element-1985" data-mp="3dgoyqhatzg7k">
                Separate terms with commas. Modules match as you type, and every
                term is searched.
              </p>
              <input id="topic-input" value={topics} onChange={e => onTopicsChange(e.target.value)} placeholder="leadership, change management, retention" className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1986" data-mp="3mh3n5pj3dbfa" />

              {(activeTerms.length > 0 || suggestedTerms.length > 0) && <div className="mt-4 rounded-lg bg-[#30718D]/5 p-4" data-id="element-1987" data-mp="eygvmhjn24gq">
                  <TopicEditor heading="Topics in this search" activeTerms={activeTerms} detectedTerms={[]} suggestedTerms={suggestedTerms} suggestedLabel={topics.trim().length > 0 ? 'Often paired with' : 'Popular topics'} vocabulary={vocabulary} onToggleTerm={onToggleTerm} emptyHint="Enter a topic above to start matching." data-id="element-1988" data-mp="iz93p7gry3bf" />
                </div>}
            </>}

          <div className="mt-5 border-t border-gray-100 pt-5" data-id="element-1989" data-mp="3evf0g4dizu28">
            <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-1990" data-mp="3etltsp2wx9u8">
              <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1991" data-mp="3at1yzs5taib9">
                Who is this for?
              </span>
              {detectedLevel && audience === detectedLevel && <span className="inline-flex items-center gap-1 text-xs text-[#30718D]" data-id="element-1992" data-mp="312pgs6lgz18a">
                  <Sparkles size={11} aria-hidden="true" data-id="element-1993" data-mp="2w5gt5qhjy4zn" />
                  Set from your notes — change it if that’s not right
                </span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-id="element-1994" data-mp="2n2xu7hgw0cio">
              {(['any', ...LEVEL_ORDER] as (AudienceLevel | 'any')[]).map(level => {
              const active = audience === level;
              const fromNotes = detectedLevel === level;
              return <button key={level} type="button" onClick={() => onAudienceChange(level)} aria-pressed={active} className={active ? 'inline-flex items-center gap-1.5 rounded-full border border-[#30718D] bg-[#30718D]/10 px-3 py-1.5 text-sm font-medium text-[#30718D] transition-colors' : 'inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50'} data-id="element-1995" data-mp="z7zr60j7buno">
                      {fromNotes && <Sparkles size={11} aria-hidden="true" data-id="element-1996" data-mp="26l9xmeg9e60d" />}
                      {level === 'any' ? 'Not sure yet' : LEVEL_LABELS[level]}
                    </button>;
            })}
            </div>
          </div>

          {/* Fixed footer: both controls always present, neither moves. */}
          <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4" data-id="element-1997" data-mp="2e758pqoz5pzq">
            <PathwayButton variant="secondary" size="sm" onClick={onBrowseAll} data-id="element-1998" data-mp="207vugwkbx5ye">
              Browse All Modules
            </PathwayButton>
            <ResetFiltersButton label="Reset search" isActive={hasSearch} onReset={onReset} data-id="element-1999" data-mp="2rxifvqu1l0o0" />
          </div>
        </div>
      </div>
    </div>;
}